import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const ChatbotSettingsModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [roles, setRoles] = useState([]);
  const [userOverrides, setUserOverrides] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/helpbot/settings');
      const { settings } = res.data;
      setGlobalEnabled(settings.globalEnabled);
      setRoles(settings.roles);
      setUserOverrides(settings.userOverrides);
    } catch (err) {
      console.error('Failed to load chatbot settings:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (isOpen) loadSettings();
  }, [isOpen, loadSettings]);

  const toggleGlobal = async () => {
    const newVal = !globalEnabled;
    setSaving('global');
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', '/helpbot/settings/global', { enabled: newVal });
      setGlobalEnabled(newVal);
    } catch (err) {
      console.error('Failed to toggle global:', err);
    } finally {
      setSaving(null);
    }
  };

  const toggleRole = async (roleId, currentVal) => {
    setSaving(`role-${roleId}`);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/helpbot/settings/role/${roleId}`, { enabled: !currentVal });
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, chatbot_enabled: !currentVal } : r));
    } catch (err) {
      console.error('Failed to toggle role:', err);
    } finally {
      setSaving(null);
    }
  };

  const toggleUser = async (userId, newVal) => {
    setSaving(`user-${userId}`);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/helpbot/settings/user/${userId}`, { enabled: newVal });
      if (newVal === null) {
        setUserOverrides(prev => prev.filter(u => u.id !== userId));
      } else {
        setUserOverrides(prev => {
          const exists = prev.find(u => u.id === userId);
          if (exists) {
            return prev.map(u => u.id === userId ? { ...u, chatbot_enabled: newVal } : u);
          }
          const fromSearch = searchResults.find(u => u.id === userId);
          if (fromSearch) return [...prev, { ...fromSearch, chatbot_enabled: newVal }];
          return prev;
        });
      }
      setSearchResults(prev => prev.map(u => u.id === userId ? { ...u, chatbot_enabled: newVal } : u));
    } catch (err) {
      console.error('Failed to toggle user:', err);
    } finally {
      setSaving(null);
    }
  };

  const searchUsers = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/helpbot/settings/users/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data.users);
    } catch (err) {
      console.error('User search failed:', err);
    } finally {
      setSearching(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    const timer = setTimeout(() => { if (searchQuery) searchUsers(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  if (!isOpen) return null;

  return (
    <div style={modalStyles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Chatbot Settings</h3>
          <button onClick={onClose} style={modalStyles.closeBtn} title="Close">&times;</button>
        </div>

        <div style={{ padding: '1.5rem', overflowY: 'auto', maxHeight: 'calc(90vh - 80px)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading settings...</div>
          ) : (
            <>
              {/* Global Toggle */}
              <div style={modalStyles.section}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', color: '#333' }}>Global Toggle</h4>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                      Master switch. When off, the chatbot is hidden for all users.
                    </p>
                  </div>
                  <button
                    onClick={toggleGlobal}
                    disabled={saving === 'global'}
                    style={{
                      ...modalStyles.toggleBtn,
                      backgroundColor: globalEnabled ? '#28a745' : '#dc3545',
                      opacity: saving === 'global' ? 0.6 : 1,
                    }}
                  >
                    {saving === 'global' ? '...' : (globalEnabled ? 'ON' : 'OFF')}
                  </button>
                </div>
              </div>

              {/* Per-Role Toggles */}
              <div style={modalStyles.section}>
                <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Enable by Role</h4>
                <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#666' }}>
                  Enable the chatbot for entire role groups. Users without a specific override will follow their role's setting.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {roles.map(role => (
                    <div key={role.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px',
                      border: '1px solid #e9ecef',
                    }}>
                      <span style={{ fontSize: '0.9rem', color: '#333' }}>{role.display_name}</span>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '6px' }}>
                        <input
                          type="checkbox"
                          checked={role.chatbot_enabled}
                          onChange={() => toggleRole(role.id, role.chatbot_enabled)}
                          disabled={saving === `role-${role.id}`}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Per-User Overrides */}
              <div style={modalStyles.section}>
                <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>User Overrides</h4>
                <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#666' }}>
                  Override the role setting for specific users. Search for a user to add an override.
                </p>

                {/* Search */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  style={modalStyles.searchInput}
                />

                {/* Search Results */}
                {searchQuery.length >= 2 && (
                  <div style={{ marginBottom: '16px' }}>
                    {searching ? (
                      <p style={{ fontSize: '0.85rem', color: '#666', margin: '8px 0' }}>Searching...</p>
                    ) : searchResults.length === 0 ? (
                      <p style={{ fontSize: '0.85rem', color: '#666', margin: '8px 0' }}>No users found.</p>
                    ) : (
                      <div style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                        {searchResults.map(user => (
                          <div key={user.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
                            backgroundColor: '#fff',
                          }}>
                            <div>
                              <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{user.display_name || user.email}</div>
                              <div style={{ fontSize: '0.8rem', color: '#888' }}>{user.role_name}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => toggleUser(user.id, true)}
                                disabled={saving === `user-${user.id}`}
                                style={{
                                  ...modalStyles.smallBtn,
                                  backgroundColor: user.chatbot_enabled === true ? '#28a745' : '#e9ecef',
                                  color: user.chatbot_enabled === true ? '#fff' : '#333',
                                }}
                              >
                                Enable
                              </button>
                              <button
                                onClick={() => toggleUser(user.id, false)}
                                disabled={saving === `user-${user.id}`}
                                style={{
                                  ...modalStyles.smallBtn,
                                  backgroundColor: user.chatbot_enabled === false ? '#dc3545' : '#e9ecef',
                                  color: user.chatbot_enabled === false ? '#fff' : '#333',
                                }}
                              >
                                Disable
                              </button>
                              {user.chatbot_enabled !== null && (
                                <button
                                  onClick={() => toggleUser(user.id, null)}
                                  disabled={saving === `user-${user.id}`}
                                  style={{ ...modalStyles.smallBtn, backgroundColor: '#ffc107', color: '#333' }}
                                  title="Remove override, follow role setting"
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Current Overrides */}
                {userOverrides.length > 0 && (
                  <div>
                    <p style={{ fontSize: '0.85rem', color: '#333', fontWeight: 500, margin: '0 0 8px 0' }}>
                      Active Overrides ({userOverrides.length})
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {userOverrides.map(user => (
                        <div key={user.id} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px',
                          border: `1px solid ${user.chatbot_enabled ? '#c3e6cb' : '#f5c6cb'}`,
                        }}>
                          <div>
                            <span style={{ fontSize: '0.875rem' }}>{user.display_name || user.email}</span>
                            <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '8px' }}>({user.role_name})</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              fontSize: '0.75rem', fontWeight: 600,
                              color: user.chatbot_enabled ? '#28a745' : '#dc3545',
                            }}>
                              {user.chatbot_enabled ? 'ENABLED' : 'DISABLED'}
                            </span>
                            <button
                              onClick={() => toggleUser(user.id, null)}
                              disabled={saving === `user-${user.id}`}
                              style={{ ...modalStyles.smallBtn, backgroundColor: '#6c757d', color: '#fff' }}
                              title="Remove override"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const modalStyles = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  container: {
    backgroundColor: 'white', borderRadius: '8px',
    width: '600px', maxWidth: '90vw', maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '1rem 1.5rem', borderBottom: '1px solid #e9ecef',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexShrink: 0,
  },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '1.5rem',
    cursor: 'pointer', color: '#6c757d', padding: 0, lineHeight: 1,
  },
  section: {
    marginBottom: '24px', paddingBottom: '24px',
    borderBottom: '1px solid #e9ecef',
  },
  toggleBtn: {
    padding: '6px 20px', border: 'none', borderRadius: '20px',
    color: 'white', fontWeight: 600, fontSize: '0.85rem',
    cursor: 'pointer', minWidth: '60px',
  },
  smallBtn: {
    padding: '4px 10px', border: 'none', borderRadius: '4px',
    fontSize: '0.75rem', cursor: 'pointer', fontWeight: 500,
  },
  searchInput: {
    width: '100%', padding: '8px 12px',
    border: '1px solid #d0d5dd', borderRadius: '6px',
    fontSize: '0.875rem', outline: 'none',
    marginBottom: '12px', boxSizing: 'border-box',
  },
};

export default ChatbotSettingsModal;
